import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Award, Leaf, HeartPulse, Users, Lightbulb, FileCheck2, MapPin } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { Reveal, Tag } from "./Reveal";
import { MPattern } from "./MPattern";
import { ASSETS } from "../../lib/assets";
import { COMPANY } from "../../lib/data";

const VALUE_ICONS = [ShieldCheck, Award, Leaf, HeartPulse, Users, Lightbulb, FileCheck2];

export const WhyChooseUs = () => {
  const { t, lang } = useLang();
  const locations = [
    { it: COMPANY.registered.short + " · Sede legale", en: COMPANY.registered.short + " · Registered office" },
    { it: COMPANY.lainate.short + " · Ufficio", en: COMPANY.lainate.short + " · Office" },
    { it: "Cantieri in tutta Europa", en: "Projects across Europe" },
  ];
  return (
    <section id="why" className="relative bg-brand-steel py-24 md:py-32 overflow-hidden" data-testid="why-choose-us">
      <MPattern opacity={0.035} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <Tag dark>{t.why.tag}</Tag>
          <h2 className="mt-5 font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.02]">
            {t.why.title}
          </h2>
          <p className="mt-6 max-w-2xl text-slate-400 text-base md:text-lg leading-relaxed">{t.why.subtitle}</p>
        </Reveal>

        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {t.why.values.map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <Reveal key={i} delay={(i % 4) * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.5 }}
                  className="h-full rounded-2xl bg-brand-navy border border-white/5 p-6"
                  data-testid={`value-card-${i}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center text-white">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-medium text-white">{v.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{v.d}</p>
                </motion.div>
              </Reveal>
            );
          })}

          {/* Locations card spanning */}
          <Reveal delay={0.1} className="sm:col-span-2 lg:col-span-1">
            <div className="relative h-full rounded-2xl overflow-hidden border border-white/10" data-testid="locations-card">
              <img src={ASSETS.inspectionTablet} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-steel via-brand-steel/60 to-transparent" />
              <div className="relative p-6 flex flex-col h-full justify-end min-h-[220px]">
                <div className="font-condensed text-xs uppercase tracking-[0.2em] text-brand-azure mb-3">{t.why.locationsTitle}</div>
                {locations.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-white text-sm py-1">
                    <MapPin size={15} className="text-brand-azure" />
                    {l[lang]}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
